import React, { useEffect, useMemo, useState } from "react"

import {
    ArrayHelpers,
    GroupOfSelectable,
    SelectorComponent,
    SelectorFieldProps,
    SelectorHelpers,
} from "@tenforce/product-fields"
import { SelectorLabels } from "@tenforce/product-fields/selectors/types"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { ISelectorField } from "../.."
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"

import {
    getChildrenForGroup,
    getGroups,
    getSelectedValues,
    getValues,
    typeHasGroups,
} from "../../../../apis/selectors"

import { findParent, isFieldItemSelect } from "./helpers"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"
import { Exceptions, Labels } from "../../../../util/Translations"
import { StatusCode } from "../../../../apis/utils/constants"
import { saveIPE } from "../../../../apis/ipe"
import { addErrorNotification } from "../../../notifications/actions"
import { formatString } from "../../../../util/Strings"
import { updateField } from "../../slice"

interface Props extends ISelectorField {
    itemId: number
    isSingleSelect: boolean
}
const {
    ClickHereToEdit,
    LoadNMore,
    LoadNMoreOf,
    NothingFound,
    FilterAllNValues,
    FilterNSelectedValues,
    ShowSelectedValues,
    ShowAllValues,
} = Labels
const {
    AnErrorOccurredWhileUpdatingThisField = 'An error occurred while updating this field: ["{0}"].',
} = Exceptions
const PAGE_SIZE = 20

export default function Selector(props: Props): JSX.Element {
    const dispatch = useAppDispatch()
    const [selectableGroup, setSelectableGroup] =
        useState<GroupOfSelectable | null>(null)
    // selectedGroup is used to display selected values
    // after toggle it holds the dirty state
    // after onSave, it is synced with the server again
    const [selectedGroup, setSelectedGroup] =
        useState<GroupOfSelectable | null>(null)
    const { isLoading: globalIsLoading } = useAppSelector(
        (state: GlobalState) => state.fields,
    )
    const [isLoading, setIsLoading] = useState(false)
    const [isSelectedShown, setIsSelectedShown] = useState(false)
    const [filter, setFilter] = useState("")

    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
    const [selectedSelectableIds, setSelectedSelectableIds] = useState<
        number[]
    >([])
    const [cachedSelectedSelectableIds, setCachedSelectedSelectableIds] =
        useState<number[]>([])
    const [totalChildrenCount, setTotalChildrenCount] = useState(0)
    const [filteredChildrenCount, setFilteredChildrenCount] = useState(0)

    const { itemId, fieldId, odataType: type, value } = props
    const fieldOptions = {
        ...props,
        isLoading: isLoading || globalIsLoading,
        description: (
            <HTMLAndInternalLinkParser
                content={(props?.description as string) ?? ""}
            />
        ),
    } as SelectorFieldProps
    const isItemSelect = isFieldItemSelect(type)
    const isGrouped = typeHasGroups(type)

    const resetValues = (): void => {
        getSelectedValues(itemId, fieldId, type).then((response) => {
            let selectedIds: number[] = []
            if (isGrouped) {
                response?.children?.forEach((child) => {
                    const idsToAdd = child.children?.map(
                        (selected) => selected.id,
                    )
                    if (idsToAdd && idsToAdd?.length > 0) {
                        selectedIds = selectedIds.concat(idsToAdd)
                    }
                })
            } else {
                selectedIds =
                    response?.children?.map((selected) => selected.id) ?? []
            }

            setSelectedSelectableIds([...selectedIds])
            setCachedSelectedSelectableIds([...selectedIds])
            setFilteredChildrenCount(selectedIds.length)
            setSelectableGroup(response)
            setSelectedGroup(response)
        })
    }

    useEffect(() => {
        resetValues()
    }, [itemId, fieldId, value])

    const getLoadMoreData = async (
        group: GroupOfSelectable,
        filter: string,
        skip: null | number = null,
        top = PAGE_SIZE,
    ): Promise<GroupOfSelectable | null> => {
        skip = skip ?? group.children?.length ?? 0
        let result: GroupOfSelectable[]

        if (isGrouped) {
            result = (
                await getChildrenForGroup(
                    group.id,
                    itemId,
                    fieldId,
                    type,
                    filter,
                    skip,
                    top,
                )
            ).children
        } else {
            result =
                (await getValues(itemId, fieldId, type, filter, skip, top))
                    ?.children ?? []
        }

        const loadedChildren = !group.children
            ? result
            : group.children.concat(result)
        const newGroup = { ...group, children: loadedChildren }
        const newSelectableGroup = SelectorHelpers.replaceInnerGroup(
            selectableGroup,
            group.id,
            newGroup,
            selectedGroupIds.concat(selectedSelectableIds),
        )
        setSelectableGroup(newSelectableGroup)
        return newGroup
    }

    const getLevelOneValues = async (
        filter = "",
    ): Promise<GroupOfSelectable | null> => {
        let newTotalChildrenCount
        let result: GroupOfSelectable | null
        if (isGrouped) {
            result = await getGroups(itemId, fieldId, type, filter)
            newTotalChildrenCount =
                result?.children
                    ?.map((child) => child.totalChildrenCount)
                    .reduce((a, b) => a + b, 0) ?? 0
        } else {
            result = await getValues(
                itemId,
                fieldId,
                type,
                filter,
                0,
                PAGE_SIZE,
            )
            newTotalChildrenCount = result?.totalChildrenCount ?? 0
        }

        setTotalChildrenCount(newTotalChildrenCount ?? 0)

        return result
    }

    const getFiltered = async (newFilter = ""): Promise<void> => {
        const result = await getLevelOneValues(newFilter)
        setFilter(newFilter.toLowerCase())

        setSelectableGroup(result)
    }

    const getAllValuesInGroup = async (
        groupId: number,
    ): Promise<GroupOfSelectable[]> => {
        return (
            SelectorHelpers.findInnerGroupById(groupId, selectableGroup)
                ?.children ?? []
        )
    }

    const loadChildrenDataForGroup = async (
        group: GroupOfSelectable,
        filter: string,
    ): Promise<void> => {
        const result = await getChildrenForGroup(
            group.id,
            itemId,
            fieldId,
            type,
            filter,
            0,
            PAGE_SIZE,
        )

        const updatedChildren =
            selectableGroup?.children?.map((currentGroup) => {
                if (currentGroup.id !== group.id) {
                    return currentGroup
                }
                return { ...group, children: [...result.children] }
            }) ?? []
        setSelectableGroup({
            ...selectableGroup,
            children: updatedChildren,
        } as GroupOfSelectable)
    }

    const onModalOpen = async (): Promise<void> => {
        const result = await getLevelOneValues("")

        if (isGrouped) {
            let newSelectedGroupsIds: number[] = []
            result?.children?.forEach((child) => {
                const childGroup = selectedGroup?.children?.find(
                    (node) => child.id === node.id,
                )
                if (
                    childGroup != null &&
                    childGroup.selectedChildrenCount ===
                        child.totalChildrenCount
                ) {
                    newSelectedGroupsIds = newSelectedGroupsIds.concat(child.id)
                }
            })

            setSelectedGroupIds(newSelectedGroupsIds)
        }

        setSelectableGroup(result)
    }

    const findValue = (
        group: GroupOfSelectable | null,
        targetId: number,
        nodeType: "group" | "selectable",
    ): GroupOfSelectable | undefined => {
        if (isGrouped) {
            // search for value in L1 or L2 of the nested groups, respectively
            return nodeType === "group"
                ? group?.children?.find((child) => child.id === targetId)
                : group?.children
                      ?.find((group) =>
                          group.children?.find(
                              (child) => child.id === targetId,
                          ),
                      )
                      ?.children?.find((child) => child.id === targetId)
        }
        // search only in L1
        return group?.children?.find((child) => child.id === targetId)
    }

    const onToggle = async (
        targetId: number,
        newForcedValue: boolean,
        nodeType: "selectable" | "group",
    ): Promise<number> => {
        let newSelectedArray: number[] = []
        if (props.isSingleSelect) {
            newSelectedArray = newForcedValue ? [targetId] : []
        } else {
            let target = findValue(selectableGroup, targetId, nodeType)

            // if the value is still not loaded in the selectableGroup
            // maybe it's present in the selected one
            if (!target) {
                const selected = findValue(selectedGroup, targetId, nodeType)
                if (selected) {
                    target = selected
                } else {
                    return 0
                }
            }

            let children = target?.children
            let childrenIds = children?.map((child) => child.id) ?? []
            // load remaining children from server
            if (
                nodeType === "group" &&
                children?.length !== target.totalChildrenCount
            ) {
                children = (
                    await getLoadMoreData(
                        target,
                        "",
                        children?.length,
                        target.totalChildrenCount,
                    )
                )?.children
                childrenIds = children?.map((child) => child.id) ?? []
            }

            if (nodeType === "group") {
                let childrenToUpdate = [...(selectedGroup?.children ?? [])]
                const indexOfGroup =
                    childrenToUpdate
                        .map((child) => child.id)
                        .indexOf(target.id) ?? -1

                // check if the group is already in selectedGroup children
                const isGroupAlreadyInSelected = indexOfGroup > -1
                if (newForcedValue) {
                    const newlySelectedSet = new Set(
                        selectedSelectableIds.concat(childrenIds),
                    )
                    newSelectedArray = Array.from(newlySelectedSet.values())

                    setSelectedGroupIds([...selectedGroupIds, targetId])
                    if (children) {
                        const newGroup = {
                            ...target,
                            children: children,
                            selectedChildrenCount: children.length,
                        }

                        // replace or add group to the level 1 children
                        if (isGroupAlreadyInSelected) {
                            childrenToUpdate = childrenToUpdate.map((child) => {
                                if (child.id !== target?.id) {
                                    return child
                                }
                                return newGroup
                            })
                        } else {
                            childrenToUpdate =
                                childrenToUpdate?.concat(newGroup)
                        }
                        setSelectedGroup({
                            ...selectedGroup,
                            children: childrenToUpdate,
                            totalChildrenCount: childrenToUpdate.length,
                        } as GroupOfSelectable)
                    }
                } else {
                    newSelectedArray = selectedSelectableIds.filter(
                        (id) => !(childrenIds.includes(id) || targetId === id),
                    )

                    // required field must contain at least one selected value
                    if (fieldOptions.isRequired && !newSelectedArray.length) {
                        return selectedSelectableIds.length
                    } else if (isGroupAlreadyInSelected) {
                        childrenToUpdate = childrenToUpdate.filter((child) => {
                            return child.id !== target?.id
                        })
                    }

                    setSelectedGroupIds([
                        ...ArrayHelpers.removeValue(targetId, selectedGroupIds),
                    ])
                    setSelectedGroup({
                        ...selectedGroup,
                        children: childrenToUpdate,
                        totalChildrenCount: childrenToUpdate.length,
                    } as GroupOfSelectable)
                }
            } else {
                newSelectedArray = newForcedValue
                    ? selectedSelectableIds.concat(targetId)
                    : ArrayHelpers.removeValue(targetId, selectedSelectableIds)
                let parent = findParent(
                    targetId,
                    selectableGroup?.children ?? [],
                )
                // if the value is still not loaded in the selectableGroup
                // maybe it's present in the selected one
                if (!parent) {
                    parent = findParent(targetId, selectedGroup?.children ?? [])
                }

                let groupsToUpdate = selectedGroup?.children ?? []

                // update selectedGroup
                if (parent) {
                    // update the parent of the "selectable"
                    const indexOfGroup =
                        groupsToUpdate
                            .map((child) => child.id)
                            .indexOf(parent.id) ?? -1

                    if (indexOfGroup > -1) {
                        const newGroup = groupsToUpdate[indexOfGroup]

                        if (newForcedValue) {
                            newGroup.children =
                                newGroup.children?.concat(target)
                        } else {
                            newGroup.children =
                                newGroup.children?.filter((child) => {
                                    return child.id !== targetId
                                }) ?? []
                        }
                        const newLength = newGroup.children?.length ?? 0
                        newGroup.selectedChildrenCount = newLength
                        newGroup.totalChildrenCount = newLength
                        groupsToUpdate[indexOfGroup] = newGroup
                        if (newGroup.children?.length === 0) {
                            groupsToUpdate = groupsToUpdate.filter((group) => {
                                return group.id !== newGroup?.id
                            })
                        }
                    } else {
                        groupsToUpdate = groupsToUpdate.concat({
                            ...parent,
                            children: [target],
                            selectedChildrenCount: 1,
                            totalChildrenCount: 1,
                        })
                    }
                } else {
                    // update selectedGroup for non-grouped selectors
                    groupsToUpdate = newForcedValue
                        ? groupsToUpdate.concat(target)
                        : groupsToUpdate.filter(
                              (child) => target?.id !== child.id,
                          )
                }
                setSelectedGroup({
                    ...selectedGroup,
                    children: groupsToUpdate,
                    totalChildrenCount: groupsToUpdate.length,
                } as GroupOfSelectable)
            }

            //select or deselect the parent group based on business logic
            if (nodeType === "selectable") {
                const parent = findParent(
                    targetId,
                    selectableGroup?.children ?? [],
                )
                const siblings = parent?.children
                if (siblings) {
                    const shouldParentBeSelected = siblings.every((sibling) =>
                        newSelectedArray.includes(sibling.id),
                    )
                    const newSelectedGroupIds = shouldParentBeSelected
                        ? selectedGroupIds.concat(parent.id)
                        : ArrayHelpers.removeValue(parent.id, selectedGroupIds)
                    setSelectedGroupIds([...newSelectedGroupIds])
                }
            }
        }

        setSelectedSelectableIds([...newSelectedArray])
        return newSelectedArray.length
    }

    const onSave = async (): Promise<void> => {
        const localSelectedChildIds = cachedSelectedSelectableIds
        const newSelectedSet = new Set(selectedSelectableIds)
        const localSelectedSet = new Set(localSelectedChildIds)
        const toDeselect = localSelectedChildIds.filter(
            (id) => !newSelectedSet.has(id),
        )
        const toSelect = selectedSelectableIds.filter(
            (id) => !localSelectedSet.has(id),
        )
        if (!toDeselect.length && !toSelect.length) {
            return
        }

        try {
            // Can't use the DataAPI to change the status as we don't support overlays yet
            if (fieldId === StatusCode) {
                const status = StatusCode.replace("SF", "")
                await saveIPE(
                    {
                        object: "Item",
                        objectId: itemId,
                        propertyId: parseInt(status),
                    },
                    toSelect[0],
                )
                // The attendees field is observing the following event to refresh itself on meetings.
                // See bug: (518520) Meeting attendees are not refreshed automatically after updating status - https://tracker.tenforce.com/workspace/823/426744f/1424600
                // PrototypeJS extends "document" to also have some event methods.
                // In order not to create a type just for this, we do a little casting magic.
                ;(document as unknown as { fire: (str: string) => void }).fire(
                    `ipe:saved:fld_${itemId}_Meeting_${status}`,
                )
            } else {
                dispatch(
                    updateField({
                        field: props,
                        newValue: selectedSelectableIds,
                    }),
                )
            }
        } catch (error) {
            // This is currently the only way to know the OldUI overlay was closed with "Cancel".
            const isCanceled =
                error === "The state transition was canceled by user"
            if (!isCanceled) {
                let errorMessage = formatString(
                    AnErrorOccurredWhileUpdatingThisField,
                    props.title,
                )
                if (error instanceof Error) {
                    errorMessage += ` ${error.message}`
                } else if (error instanceof String) {
                    errorMessage += ` ${error}`
                }
                dispatch(addErrorNotification(errorMessage))
            } else {
                setSelectedSelectableIds(cachedSelectedSelectableIds)
            }
        } finally {
            setIsLoading(false)
            setFilter("")
        }
    }

    const selectorLabels = {
        EmptyPlaceholder: ClickHereToEdit,
        FilterAllNValues: (count) =>
            Symbols.Search + formatString(FilterAllNValues, count),
        FilterNSelectedValues: (count) =>
            Symbols.Search + formatString(FilterNSelectedValues, count),
        FilterNoResults: NothingFound,
        LoadNMore: (remaining) => formatString(LoadNMore, remaining),
        LoadNMoreOf: (pageSize, remaining) =>
            formatString(LoadNMoreOf, pageSize, remaining),
        ShowAllValuesTooltip: ShowAllValues,
        ShowSelectedValuesTooltip: ShowSelectedValues,
    } as SelectorLabels

    const filteredSelectedGroup = useMemo(() => {
        if (filter === "") {
            setFilteredChildrenCount(selectedSelectableIds.length)
            return selectedGroup
        }
        let childrenCount = 0
        let filteredSelected: GroupOfSelectable | null

        if (isGrouped) {
            const filteredGroups = selectedGroup?.children
                ?.filter(
                    (group) =>
                        group.name.toLowerCase().includes(filter) ||
                        group.children?.some((child) =>
                            child.name.toLowerCase().includes(filter),
                        ),
                )
                ?.map((group) => {
                    const filteredChildren =
                        group.children?.filter(
                            (child) =>
                                group.name.toLowerCase().includes(filter) ||
                                child.name.toLowerCase().includes(filter),
                        ) ?? []
                    childrenCount += filteredChildren.length
                    return {
                        ...group,
                        totalChildrenCount: filteredChildren.length,
                        children: filteredChildren,
                    } as GroupOfSelectable
                })

            filteredSelected = {
                ...selectedGroup,
                children: filteredGroups,
                totalChildrenCount: filteredGroups?.length ?? 0,
            } as GroupOfSelectable
        } else {
            const filtered = selectedGroup?.children?.filter((child) =>
                child.name.toLowerCase().includes(filter),
            )
            childrenCount = filtered?.length ?? 0
            filteredSelected = {
                ...selectedGroup,
                children: filtered,
                totalChildrenCount: childrenCount,
            } as GroupOfSelectable
        }
        setFilteredChildrenCount(childrenCount)
        return filteredSelected
    }, [filter, selectableGroup, selectedSelectableIds])

    return (
        <SelectorComponent
            displayedSelected={selectedGroup}
            group={isSelectedShown ? filteredSelectedGroup : selectableGroup}
            shouldTreatGroupAsSelectableValue={isItemSelect}
            shouldExpandGroups={!isItemSelect}
            selectedGroupIds={selectedGroupIds}
            selectedValueIds={selectedSelectableIds}
            getLoadMoreData={async (
                group: GroupOfSelectable,
                filter: string,
            ): Promise<void> => {
                await getLoadMoreData(group, filter, group.children?.length)
            }}
            loadChildrenDataForGroup={loadChildrenDataForGroup}
            getAllValuesInGroup={getAllValuesInGroup}
            onLoadingStateChanged={setIsLoading}
            onSave={onSave}
            onToggle={onToggle}
            getFiltered={getFiltered}
            onViewChange={async (isSelectedShown: boolean): Promise<void> => {
                setIsSelectedShown(isSelectedShown)
            }}
            onModalOpen={onModalOpen}
            isGrouped={isGrouped}
            isSingleSelect={props.isSingleSelect}
            fieldOptions={fieldOptions}
            labels={selectorLabels}
            totalChildrenCount={totalChildrenCount}
            filteredChildrenCount={filteredChildrenCount}
            onEscapePressed={resetValues}
        />
    )
}
